// Dados de editais populares pré-estruturados
// Fonte: Editais públicos e estrutura comum de concursos brasileiros

export interface SeedExam {
  institution: string;
  role: string;
  year: number;
  banca: string;
  level: 'medio' | 'superior';
  area: string; // Área para agrupamento
  aliases?: string[]; // Siglas/apelidos
  subjects: {
    name: string;
    topics: string[];
    weight?: number;
  }[];
}

// ==================== MATÉRIAS BASE POR ÁREA ====================

export const SUBJECTS_BY_AREA: { [area: string]: { name: string; topics: string[] }[] } = {
  'tribunais': [
    {
      name: 'Língua Portuguesa',
      topics: ['Interpretação de texto', 'Ortografia', 'Acentuação', 'Concordância verbal e nominal', 'Regência verbal e nominal', 'Pontuação', 'Classes de palavras', 'Sintaxe', 'Semântica', 'Redação oficial']
    },
    {
      name: 'Raciocínio Lógico',
      topics: ['Proposições', 'Conectivos lógicos', 'Tabelas-verdade', 'Equivalências', 'Negação de proposições', 'Diagramas lógicos', 'Sequências', 'Probabilidade', 'Análise combinatória']
    },
    {
      name: 'Noções de Informática',
      topics: ['Sistema operacional Windows', 'Microsoft Office (Word, Excel, PowerPoint)', 'Internet e navegadores', 'Segurança da informação', 'Redes de computadores', 'Correio eletrônico']
    },
    {
      name: 'Direito Constitucional',
      topics: ['Princípios fundamentais', 'Direitos e garantias fundamentais', 'Organização do Estado', 'Organização dos Poderes', 'Poder Judiciário', 'Funções essenciais à Justiça', 'Controle de constitucionalidade']
    },
    {
      name: 'Direito Administrativo',
      topics: ['Princípios da Administração Pública', 'Atos administrativos', 'Poderes administrativos', 'Agentes públicos', 'Licitações e contratos', 'Serviços públicos', 'Responsabilidade civil do Estado', 'Controle da Administração']
    },
    {
      name: 'Direito Processual Civil',
      topics: ['Normas processuais civis', 'Jurisdição e competência', 'Partes e procuradores', 'Atos processuais', 'Tutela provisória', 'Procedimento comum', 'Recursos', 'Execução']
    },
    {
      name: 'Direito Penal',
      topics: ['Princípios do Direito Penal', 'Aplicação da lei penal', 'Crime', 'Culpabilidade', 'Penas', 'Crimes contra a Administração Pública']
    }
  ],

  'policial': [
    {
      name: 'Língua Portuguesa',
      topics: ['Interpretação de texto', 'Ortografia', 'Acentuação', 'Concordância verbal e nominal', 'Regência', 'Pontuação', 'Classes de palavras', 'Sintaxe']
    },
    {
      name: 'Raciocínio Lógico',
      topics: ['Proposições', 'Conectivos lógicos', 'Tabelas-verdade', 'Equivalências', 'Negação', 'Diagramas lógicos', 'Sequências']
    },
    {
      name: 'Noções de Informática',
      topics: ['Windows', 'Linux', 'Microsoft Office', 'Internet', 'Segurança da informação', 'Redes']
    },
    {
      name: 'Direito Constitucional',
      topics: ['Princípios fundamentais', 'Direitos e garantias fundamentais', 'Segurança pública', 'Organização do Estado', 'Nacionalidade', 'Direitos políticos']
    },
    {
      name: 'Direito Administrativo',
      topics: ['Princípios', 'Atos administrativos', 'Poderes administrativos', 'Agentes públicos', 'Responsabilidade civil']
    },
    {
      name: 'Direito Penal',
      topics: ['Princípios', 'Aplicação da lei penal', 'Crime', 'Imputabilidade', 'Concurso de pessoas', 'Penas', 'Crimes contra a pessoa', 'Crimes contra o patrimônio', 'Crimes contra a Administração Pública']
    },
    {
      name: 'Direito Processual Penal',
      topics: ['Inquérito policial', 'Ação penal', 'Prisão e liberdade provisória', 'Provas', 'Juiz, Ministério Público, Defensor', 'Citação e intimação']
    },
    {
      name: 'Legislação Especial',
      topics: ['Lei de Drogas', 'Estatuto do Desarmamento', 'Crimes hediondos', 'Lei Maria da Penha', 'Estatuto da Criança e Adolescente', 'Lei de Abuso de Autoridade']
    }
  ],

  'fiscal': [
    {
      name: 'Língua Portuguesa',
      topics: ['Interpretação de texto', 'Ortografia', 'Concordância', 'Regência', 'Pontuação', 'Redação oficial']
    },
    {
      name: 'Raciocínio Lógico',
      topics: ['Proposições', 'Conectivos', 'Tabelas-verdade', 'Equivalências', 'Quantificadores', 'Diagramas']
    },
    {
      name: 'Matemática Financeira',
      topics: ['Juros simples', 'Juros compostos', 'Taxas de juros', 'Descontos', 'Séries de pagamentos', 'Sistemas de amortização', 'Fluxo de caixa']
    },
    {
      name: 'Estatística',
      topics: ['Estatística descritiva', 'Medidas de posição', 'Medidas de dispersão', 'Probabilidade', 'Distribuições', 'Amostragem']
    },
    {
      name: 'Direito Constitucional',
      topics: ['Princípios fundamentais', 'Direitos e garantias', 'Tributação e orçamento', 'Sistema tributário nacional', 'Finanças públicas']
    },
    {
      name: 'Direito Administrativo',
      topics: ['Princípios', 'Atos administrativos', 'Processo administrativo', 'Licitações', 'Contratos', 'Servidores públicos']
    },
    {
      name: 'Direito Tributário',
      topics: ['Sistema tributário nacional', 'Competência tributária', 'Limitações ao poder de tributar', 'Impostos federais, estaduais e municipais', 'Obrigação tributária', 'Crédito tributário', 'Administração tributária']
    },
    {
      name: 'Contabilidade Geral',
      topics: ['Patrimônio', 'Escrituração', 'Balanço patrimonial', 'DRE', 'Demonstração de fluxos de caixa', 'Análise de balanços']
    },
    {
      name: 'Contabilidade Pública',
      topics: ['Orçamento público', 'Receita pública', 'Despesa pública', 'Lei 4.320/64', 'LRF', 'SIAFI']
    },
    {
      name: 'Auditoria',
      topics: ['Normas de auditoria', 'Planejamento', 'Procedimentos', 'Papéis de trabalho', 'Parecer', 'Auditoria governamental']
    }
  ],

  'bancario': [
    {
      name: 'Língua Portuguesa',
      topics: ['Interpretação de texto', 'Ortografia', 'Concordância', 'Regência', 'Pontuação', 'Coesão e coerência']
    },
    {
      name: 'Raciocínio Lógico',
      topics: ['Proposições', 'Conectivos', 'Tabelas-verdade', 'Sequências', 'Problemas aritméticos']
    },
    {
      name: 'Matemática Financeira',
      topics: ['Juros simples', 'Juros compostos', 'Taxas equivalentes', 'Descontos', 'Séries de pagamentos', 'Sistemas de amortização']
    },
    {
      name: 'Conhecimentos Bancários',
      topics: ['Sistema Financeiro Nacional', 'Banco Central', 'CMN', 'Produtos bancários', 'Operações de crédito', 'Mercado de capitais', 'Mercado de câmbio', 'Seguros', 'Previdência', 'Capitalização']
    },
    {
      name: 'Atendimento',
      topics: ['Marketing em empresas de serviços', 'Satisfação e retenção de clientes', 'Valor percebido', 'Técnicas de vendas', 'Etiqueta empresarial', 'Resolução CMN 4.860/2020']
    },
    {
      name: 'Noções de Informática',
      topics: ['Windows', 'Microsoft Office', 'Internet', 'Segurança da informação']
    },
    {
      name: 'Conhecimentos de Informática',
      topics: ['Editor de textos', 'Planilhas', 'Internet', 'Segurança', 'Sistemas operacionais']
    }
  ],

  'administrativo': [
    {
      name: 'Língua Portuguesa',
      topics: ['Interpretação de texto', 'Ortografia', 'Acentuação', 'Concordância', 'Regência', 'Pontuação', 'Redação oficial']
    },
    {
      name: 'Raciocínio Lógico',
      topics: ['Proposições', 'Conectivos', 'Tabelas-verdade', 'Equivalências', 'Sequências', 'Problemas aritméticos']
    },
    {
      name: 'Noções de Informática',
      topics: ['Windows', 'Microsoft Office', 'Internet', 'Correio eletrônico', 'Segurança da informação']
    },
    {
      name: 'Noções de Direito Constitucional',
      topics: ['Princípios fundamentais', 'Direitos e garantias fundamentais', 'Organização do Estado', 'Administração Pública']
    },
    {
      name: 'Noções de Direito Administrativo',
      topics: ['Princípios', 'Atos administrativos', 'Agentes públicos', 'Licitações', 'Contratos administrativos']
    },
    {
      name: 'Ética no Serviço Público',
      topics: ['Código de Ética Profissional', 'Lei 8.112/90', 'Regime disciplinar', 'Deveres e proibições']
    },
    {
      name: 'Legislação Específica',
      topics: ['Lei 8.112/90', 'Lei 9.784/99', 'Lei de Acesso à Informação', 'LGPD']
    }
  ],

  'saude': [
    {
      name: 'Língua Portuguesa',
      topics: ['Interpretação de texto', 'Ortografia', 'Concordância', 'Regência', 'Pontuação']
    },
    {
      name: 'Raciocínio Lógico',
      topics: ['Proposições', 'Conectivos', 'Tabelas-verdade', 'Sequências']
    },
    {
      name: 'Noções de Informática',
      topics: ['Windows', 'Microsoft Office', 'Internet', 'Segurança da informação']
    },
    {
      name: 'Legislação do SUS',
      topics: ['Lei 8.080/90', 'Lei 8.142/90', 'Princípios e diretrizes do SUS', 'Financiamento', 'Controle social', 'NOB', 'NOAS', 'Pacto pela Saúde']
    },
    {
      name: 'Políticas de Saúde',
      topics: ['Atenção básica', 'Estratégia Saúde da Família', 'Vigilância em saúde', 'Promoção da saúde', 'Humanização']
    },
    {
      name: 'Noções de Direito Constitucional',
      topics: ['Direito à saúde', 'Seguridade social', 'Organização do Estado']
    },
    {
      name: 'Noções de Direito Administrativo',
      topics: ['Princípios', 'Atos administrativos', 'Agentes públicos', 'Licitações']
    }
  ]
};

// ==================== EDITAIS PRÉ-CADASTRADOS ====================

export const SEED_EXAMS: SeedExam[] = [
  // INSS
  {
    institution: 'INSS',
    role: 'Técnico do Seguro Social',
    year: 2024,
    banca: 'Cebraspe',
    level: 'medio',
    area: 'administrativo',
    subjects: [
      ...SUBJECTS_BY_AREA['administrativo'],
      {
        name: 'Seguridade Social',
        topics: ['Origem e evolução', 'Conceituação', 'Organização e princípios', 'Legislação previdenciária', 'Regime Geral de Previdência Social', 'Benefícios', 'Custeio']
      }
    ]
  },
  {
    institution: 'INSS',
    role: 'Analista do Seguro Social',
    year: 2024,
    banca: 'Cebraspe',
    level: 'superior',
    area: 'administrativo',
    subjects: [
      ...SUBJECTS_BY_AREA['administrativo'],
      {
        name: 'Direito Previdenciário',
        topics: ['Seguridade Social', 'Regime Geral de Previdência', 'Benefícios', 'Custeio', 'Salário de contribuição', 'Decadência e prescrição']
      }
    ]
  },

  // Polícia Federal
  {
    institution: 'Polícia Federal',
    role: 'Agente',
    year: 2024,
    banca: 'Cebraspe',
    level: 'superior',
    area: 'policial',
    aliases: ['PF', 'Polícia Federal', 'DPF'],
    subjects: SUBJECTS_BY_AREA['policial']
  },
  {
    institution: 'Polícia Federal',
    role: 'Escrivão',
    year: 2024,
    banca: 'Cebraspe',
    level: 'superior',
    area: 'policial',
    aliases: ['PF', 'Polícia Federal', 'DPF'],
    subjects: SUBJECTS_BY_AREA['policial']
  },
  {
    institution: 'Polícia Federal',
    role: 'Delegado',
    year: 2024,
    banca: 'Cebraspe',
    level: 'superior',
    area: 'policial',
    aliases: ['PF', 'Polícia Federal', 'DPF'],
    subjects: [
      ...SUBJECTS_BY_AREA['policial'],
      {
        name: 'Direito Civil',
        topics: ['Lei de Introdução', 'Pessoas', 'Bens', 'Fatos jurídicos', 'Obrigações', 'Contratos', 'Responsabilidade civil']
      },
      {
        name: 'Direito Empresarial',
        topics: ['Teoria geral', 'Sociedades', 'Títulos de crédito', 'Contratos mercantis', 'Falência e recuperação']
      }
    ]
  },

  // PRF
  {
    institution: 'Polícia Rodoviária Federal',
    role: 'Policial Rodoviário Federal',
    year: 2024,
    banca: 'Cebraspe',
    level: 'superior',
    area: 'policial',
    aliases: ['PRF', 'Polícia Rodoviária Federal'],
    subjects: [
      ...SUBJECTS_BY_AREA['policial'],
      {
        name: 'Legislação de Trânsito',
        topics: ['Código de Trânsito Brasileiro', 'Infrações', 'Penalidades', 'Medidas administrativas', 'Crimes de trânsito', 'Sinalização']
      },
      {
        name: 'Física',
        topics: ['Mecânica', 'Cinemática', 'Dinâmica', 'Colisões', 'Atrito', 'Força centrípeta']
      }
    ]
  },

  // Receita Federal
  {
    institution: 'Receita Federal',
    role: 'Auditor Fiscal',
    year: 2024,
    banca: 'FGV',
    level: 'superior',
    area: 'fiscal',
    subjects: SUBJECTS_BY_AREA['fiscal']
  },
  {
    institution: 'Receita Federal',
    role: 'Analista Tributário',
    year: 2024,
    banca: 'FGV',
    level: 'superior',
    area: 'fiscal',
    subjects: SUBJECTS_BY_AREA['fiscal'].filter(s => !['Auditoria'].includes(s.name))
  },

  // Banco Central (BACEN)
  {
    institution: 'Banco Central do Brasil',
    role: 'Analista do BACEN',
    year: 2025,
    banca: 'a definir',
    level: 'superior',
    area: 'fiscal',
    aliases: ['BACEN', 'BCB', 'BC'],
    subjects: [
      ...SUBJECTS_BY_AREA['fiscal'],
      {
        name: 'Economia',
        topics: ['Macroeconomia', 'Microeconomia', 'Política monetária', 'Inflação', 'Câmbio', 'Contas nacionais']
      },
      {
        name: 'Finanças Públicas',
        topics: ['Orçamento público', 'Receita e despesa pública', 'Resultado fiscal', 'Lei de Responsabilidade Fiscal']
      }
    ]
  },
  {
    institution: 'Banco Central do Brasil',
    role: 'Técnico do BACEN',
    year: 2025,
    banca: 'a definir',
    level: 'medio',
    area: 'fiscal',
    aliases: ['BACEN', 'BCB', 'BC'],
    subjects: [
      ...SUBJECTS_BY_AREA['administrativo'],
      {
        name: 'Conhecimentos Bancários',
        topics: ['Sistema Financeiro Nacional', 'Banco Central', 'CMN', 'Produtos bancários', 'Mercado de capitais']
      }
    ]
  },

  // Polícia Civil (genérico)
  {
    institution: 'Polícia Civil',
    role: 'Inspetor / Investigador',
    year: 2024,
    banca: 'a definir',
    level: 'superior',
    area: 'policial',
    aliases: ['PC', 'Polícia Civil'],
    subjects: SUBJECTS_BY_AREA['policial']
  },

  // Polícia Militar (genérico)
  {
    institution: 'Polícia Militar',
    role: 'Soldado / Oficial',
    year: 2024,
    banca: 'a definir',
    level: 'medio',
    area: 'policial',
    aliases: ['PM', 'Polícia Militar'],
    subjects: SUBJECTS_BY_AREA['policial']
  },

  // Tribunais
  {
    institution: 'TJ-RJ',
    role: 'Técnico Judiciário',
    year: 2024,
    banca: 'FGV',
    level: 'medio',
    area: 'tribunais',
    subjects: SUBJECTS_BY_AREA['tribunais'].filter(s => s.name !== 'Direito Processual Civil')
  },
  {
    institution: 'TJ-RJ',
    role: 'Analista Judiciário',
    year: 2024,
    banca: 'FGV',
    level: 'superior',
    area: 'tribunais',
    subjects: SUBJECTS_BY_AREA['tribunais']
  },
  {
    institution: 'TJ-SP',
    role: 'Escrevente Técnico Judiciário',
    year: 2024,
    banca: 'VUNESP',
    level: 'medio',
    area: 'tribunais',
    subjects: SUBJECTS_BY_AREA['tribunais'].filter(s => s.name !== 'Direito Penal')
  },
  {
    institution: 'TRF-3',
    role: 'Técnico Judiciário',
    year: 2024,
    banca: 'FCC',
    level: 'medio',
    area: 'tribunais',
    subjects: SUBJECTS_BY_AREA['tribunais']
  },
  {
    institution: 'TRF-3',
    role: 'Analista Judiciário',
    year: 2024,
    banca: 'FCC',
    level: 'superior',
    area: 'tribunais',
    subjects: SUBJECTS_BY_AREA['tribunais']
  },
  {
    institution: 'TRT-2',
    role: 'Técnico Judiciário',
    year: 2024,
    banca: 'FCC',
    level: 'medio',
    area: 'tribunais',
    subjects: [
      ...SUBJECTS_BY_AREA['tribunais'].filter(s => s.name !== 'Direito Penal'),
      {
        name: 'Direito do Trabalho',
        topics: ['Princípios', 'Contrato de trabalho', 'Jornada', 'Férias', 'Remuneração', 'Rescisão', 'FGTS', 'Segurança e medicina do trabalho']
      },
      {
        name: 'Direito Processual do Trabalho',
        topics: ['Organização da Justiça do Trabalho', 'Competência', 'Partes', 'Procedimentos', 'Recursos', 'Execução trabalhista']
      }
    ]
  },

  // Bancos
  {
    institution: 'Banco do Brasil',
    role: 'Escriturário',
    year: 2024,
    banca: 'Cesgranrio',
    level: 'medio',
    area: 'bancario',
    subjects: SUBJECTS_BY_AREA['bancario']
  },
  {
    institution: 'Caixa Econômica Federal',
    role: 'Técnico Bancário',
    year: 2024,
    banca: 'Cesgranrio',
    level: 'medio',
    area: 'bancario',
    subjects: SUBJECTS_BY_AREA['bancario']
  },
  {
    institution: 'Banco Central',
    role: 'Analista',
    year: 2024,
    banca: 'Cebraspe',
    level: 'superior',
    area: 'fiscal',
    subjects: [
      ...SUBJECTS_BY_AREA['fiscal'],
      {
        name: 'Economia',
        topics: ['Microeconomia', 'Macroeconomia', 'Economia monetária', 'Economia internacional', 'Economia brasileira']
      }
    ]
  },

  // Área de Saúde
  {
    institution: 'Ministério da Saúde',
    role: 'Técnico Administrativo',
    year: 2024,
    banca: 'Cebraspe',
    level: 'medio',
    area: 'saude',
    subjects: SUBJECTS_BY_AREA['saude']
  },

  // Concursos Estaduais/Municipais populares
  {
    institution: 'Prefeitura Municipal',
    role: 'Agente Administrativo',
    year: 2024,
    banca: 'Diversas',
    level: 'medio',
    area: 'administrativo',
    subjects: SUBJECTS_BY_AREA['administrativo']
  },
  {
    institution: 'Secretaria de Educação',
    role: 'Professor',
    year: 2024,
    banca: 'Diversas',
    level: 'superior',
    area: 'administrativo',
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Redação'] },
      { name: 'Conhecimentos Pedagógicos', topics: ['LDB', 'BNCC', 'Didática', 'Avaliação', 'Planejamento', 'Teorias da aprendizagem'] },
      { name: 'Legislação Educacional', topics: ['LDB 9.394/96', 'ECA', 'Constituição Federal - Educação', 'PNE'] }
    ]
  },

  // Polícias Estaduais
  {
    institution: 'Polícia Civil',
    role: 'Investigador',
    year: 2024,
    banca: 'Diversas',
    level: 'superior',
    area: 'policial',
    subjects: SUBJECTS_BY_AREA['policial']
  },
  {
    institution: 'Polícia Civil',
    role: 'Escrivão',
    year: 2024,
    banca: 'Diversas',
    level: 'superior',
    area: 'policial',
    subjects: SUBJECTS_BY_AREA['policial']
  },
  {
    institution: 'Polícia Militar',
    role: 'Soldado',
    year: 2024,
    banca: 'Diversas',
    level: 'medio',
    area: 'policial',
    subjects: SUBJECTS_BY_AREA['policial'].filter(s => !['Direito Processual Penal', 'Legislação Especial'].includes(s.name))
  },

  // CGU
  {
    institution: 'CGU',
    role: 'Auditor Federal de Finanças e Controle',
    year: 2024,
    banca: 'Cebraspe',
    level: 'superior',
    area: 'fiscal',
    subjects: SUBJECTS_BY_AREA['fiscal']
  },

  // TCU
  {
    institution: 'TCU',
    role: 'Auditor Federal de Controle Externo',
    year: 2024,
    banca: 'Cebraspe',
    level: 'superior',
    area: 'fiscal',
    subjects: [
      ...SUBJECTS_BY_AREA['fiscal'],
      {
        name: 'Controle Externo',
        topics: ['Sistemas de controle', 'Controle externo no Brasil', 'Tribunais de Contas', 'Lei Orgânica do TCU', 'Regimento Interno']
      }
    ]
  },

  // Reforço inicial para buscas por sigla/estado no onboarding
  {
    institution: 'Polícia Civil de São Paulo',
    role: 'Investigador de Polícia',
    year: 2025,
    banca: 'VUNESP',
    level: 'superior',
    area: 'policial',
    aliases: ['PC-SP', 'PCSP', 'Polícia Civil SP'],
    subjects: SUBJECTS_BY_AREA['policial']
  },
  {
    institution: 'Polícia Militar de São Paulo',
    role: 'Soldado',
    year: 2025,
    banca: 'VUNESP',
    level: 'medio',
    area: 'policial',
    aliases: ['PM-SP', 'PMSP', 'Polícia Militar SP'],
    subjects: SUBJECTS_BY_AREA['policial'].filter(s => !['Direito Processual Penal', 'Legislação Especial'].includes(s.name))
  },
  {
    institution: 'Polícia Civil de Minas Gerais',
    role: 'Escrivão',
    year: 2025,
    banca: 'FGV',
    level: 'superior',
    area: 'policial',
    aliases: ['PC-MG', 'PCMG', 'Polícia Civil MG'],
    subjects: SUBJECTS_BY_AREA['policial']
  },
  {
    institution: 'Polícia Militar de Minas Gerais',
    role: 'Soldado',
    year: 2025,
    banca: 'FGV',
    level: 'medio',
    area: 'policial',
    aliases: ['PM-MG', 'PMMG', 'Polícia Militar MG'],
    subjects: SUBJECTS_BY_AREA['policial'].filter(s => !['Direito Processual Penal', 'Legislação Especial'].includes(s.name))
  },
  {
    institution: 'TJ-MG',
    role: 'Oficial Judiciário',
    year: 2025,
    banca: 'IBFC',
    level: 'medio',
    area: 'tribunais',
    aliases: ['TJMG', 'TJ-MG', 'Tribunal de Justiça MG'],
    subjects: SUBJECTS_BY_AREA['tribunais']
  },
  {
    institution: 'TRT-15',
    role: 'Técnico Judiciário',
    year: 2025,
    banca: 'FCC',
    level: 'medio',
    area: 'tribunais',
    aliases: ['TRT15', 'TRT-15', 'Tribunal Regional do Trabalho 15'],
    subjects: [
      ...SUBJECTS_BY_AREA['tribunais'].filter(s => s.name !== 'Direito Penal'),
      {
        name: 'Direito do Trabalho',
        topics: ['Princípios', 'Contrato de trabalho', 'Jornada', 'Férias', 'Remuneração', 'Rescisão', 'FGTS']
      },
      {
        name: 'Direito Processual do Trabalho',
        topics: ['Competência', 'Partes', 'Atos processuais', 'Recursos', 'Execução trabalhista']
      }
    ]
  },

  // PETROBRAS (pacote inicial amplo)
  {
    institution: 'PETROBRAS',
    role: 'Técnico de Administração e Controle Júnior',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'medio',
    area: 'administrativo',
    aliases: ['petrobras', 'petro', 'petrobras administrativo'],
    subjects: [
      ...SUBJECTS_BY_AREA['administrativo'],
      {
        name: 'Noções de Administração',
        topics: ['Funções administrativas', 'Processos organizacionais', 'Gestão de pessoas', 'Comunicação organizacional']
      }
    ]
  },
  {
    institution: 'PETROBRAS',
    role: 'Técnico de Segurança Júnior',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'medio',
    area: 'administrativo',
    aliases: ['petrobras', 'petro', 'petrobras seguranca'],
    subjects: [
      ...SUBJECTS_BY_AREA['administrativo'],
      {
        name: 'Segurança do Trabalho',
        topics: ['NRs principais', 'Análise de risco', 'Prevenção de acidentes', 'Investigação de acidentes', 'EPI e EPC']
      },
      {
        name: 'Higiene Ocupacional',
        topics: ['Agentes físicos', 'Agentes químicos', 'Agentes biológicos', 'Avaliação e controle ambiental']
      }
    ]
  },
  {
    institution: 'PETROBRAS',
    role: 'Técnico de Operação Júnior',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'medio',
    area: 'administrativo',
    aliases: ['petrobras', 'petro', 'petrobras operacao'],
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Concordância', 'Regência', 'Pontuação'] },
      { name: 'Matemática', topics: ['Razão e proporção', 'Porcentagem', 'Equações', 'Funções', 'Estatística básica'] },
      { name: 'Noções de Informática', topics: ['Windows', 'Office', 'Internet', 'Segurança da informação'] },
      {
        name: 'Operação de Processos Industriais',
        topics: ['Instrumentação básica', 'Operações unitárias', 'Leitura de P&ID', 'Controle de processo', 'Rotinas operacionais']
      }
    ]
  },
  {
    institution: 'PETROBRAS',
    role: 'Técnico de Manutenção Júnior (Mecânica)',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'medio',
    area: 'administrativo',
    aliases: ['petrobras', 'petro', 'petrobras manutencao mecanica'],
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Concordância', 'Regência', 'Pontuação'] },
      { name: 'Matemática', topics: ['Razão e proporção', 'Porcentagem', 'Equações', 'Geometria', 'Trigonometria básica'] },
      {
        name: 'Mecânica',
        topics: ['Elementos de máquinas', 'Metrologia', 'Desenho técnico', 'Materiais', 'Manutenção preventiva e corretiva']
      },
      { name: 'Segurança do Trabalho', topics: ['NR-10', 'NR-12', 'NR-33', 'NR-35', 'Permissão de trabalho'] }
    ]
  },
  {
    institution: 'PETROBRAS',
    role: 'Técnico de Manutenção Júnior (Elétrica)',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'medio',
    area: 'administrativo',
    aliases: ['petrobras', 'petro', 'petrobras manutencao eletrica'],
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Concordância', 'Regência', 'Pontuação'] },
      { name: 'Matemática', topics: ['Razão e proporção', 'Porcentagem', 'Equações', 'Funções', 'Trigonometria básica'] },
      {
        name: 'Eletricidade',
        topics: ['Circuitos elétricos', 'Máquinas elétricas', 'Comandos elétricos', 'Proteções', 'Instrumentação elétrica']
      },
      { name: 'Segurança do Trabalho', topics: ['NR-10', 'NR-33', 'NR-35', 'Bloqueio e etiquetagem'] }
    ]
  },
  {
    institution: 'PETROBRAS',
    role: 'Técnico de Manutenção Júnior (Instrumentação)',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'medio',
    area: 'administrativo',
    aliases: ['petrobras', 'petro', 'petrobras instrumentacao'],
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Concordância', 'Regência', 'Pontuação'] },
      { name: 'Matemática', topics: ['Razão e proporção', 'Porcentagem', 'Equações', 'Funções', 'Estatística básica'] },
      {
        name: 'Instrumentação Industrial',
        topics: ['Sensores e transmissores', 'Malhas de controle', 'CLP', 'Calibração', 'Sistemas supervisórios']
      },
      { name: 'Segurança do Trabalho', topics: ['NR-10', 'NR-33', 'NR-35', 'Permissão de trabalho'] }
    ]
  },
  {
    institution: 'PETROBRAS',
    role: 'Engenheiro de Produção Júnior',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'superior',
    area: 'fiscal',
    aliases: ['petrobras', 'petro', 'petrobras engenheiro producao'],
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Redação oficial'] },
      { name: 'Inglês Técnico', topics: ['Leitura técnica', 'Vocabulário técnico', 'Interpretação de textos'] },
      {
        name: 'Engenharia de Produção',
        topics: ['Pesquisa operacional', 'Planejamento e controle da produção', 'Logística', 'Qualidade', 'Gestão de projetos']
      },
      { name: 'Economia e Finanças', topics: ['Análise de investimentos', 'Custos industriais', 'Indicadores econômicos'] }
    ]
  },
  {
    institution: 'PETROBRAS',
    role: 'Engenheiro Mecânico Júnior',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'superior',
    area: 'fiscal',
    aliases: ['petrobras', 'petro', 'petrobras engenheiro mecanico'],
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Redação oficial'] },
      { name: 'Inglês Técnico', topics: ['Leitura técnica', 'Vocabulário técnico'] },
      {
        name: 'Engenharia Mecânica',
        topics: ['Mecânica dos sólidos', 'Termodinâmica', 'Transferência de calor', 'Máquinas térmicas', 'Materiais']
      },
      { name: 'Manutenção Industrial', topics: ['Confiabilidade', 'Manutenção preditiva', 'Análise de falhas'] }
    ]
  },
  {
    institution: 'PETROBRAS',
    role: 'Engenheiro Eletricista Júnior',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'superior',
    area: 'fiscal',
    aliases: ['petrobras', 'petro', 'petrobras engenheiro eletricista'],
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Redação oficial'] },
      { name: 'Inglês Técnico', topics: ['Leitura técnica', 'Vocabulário técnico'] },
      {
        name: 'Engenharia Elétrica',
        topics: ['Circuitos', 'Sistemas elétricos de potência', 'Máquinas elétricas', 'Proteção de sistemas', 'Eletrônica de potência']
      },
      { name: 'Normas e Segurança', topics: ['NR-10', 'Aterramento', 'SPDA', 'Eficiência energética'] }
    ]
  },
  {
    institution: 'PETROBRAS',
    role: 'Engenheiro Químico Júnior',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'superior',
    area: 'fiscal',
    aliases: ['petrobras', 'petro', 'petrobras engenheiro quimico'],
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Redação oficial'] },
      { name: 'Inglês Técnico', topics: ['Leitura técnica', 'Vocabulário técnico'] },
      {
        name: 'Engenharia Química',
        topics: ['Fenômenos de transporte', 'Termodinâmica química', 'Cinética e reatores', 'Operações unitárias', 'Processos de refino']
      },
      { name: 'Segurança de Processos', topics: ['Análise de risco', 'HAZOP', 'Integridade de ativos'] }
    ]
  },

  // TRANSPETRO
  {
    institution: 'TRANSPETRO',
    role: 'Técnico de Operação Júnior',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'medio',
    area: 'administrativo',
    aliases: ['transpetro', 'trans', 'petrobras transporte'],
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Concordância', 'Regência', 'Pontuação'] },
      { name: 'Matemática', topics: ['Razão e proporção', 'Porcentagem', 'Equações', 'Funções', 'Estatística básica'] },
      { name: 'Noções de Informática', topics: ['Windows', 'Office', 'Internet', 'Segurança da informação'] },
      {
        name: 'Operação de Transporte e Dutos',
        topics: ['Noções de logística de petróleo e derivados', 'Operação de terminais', 'Bombeamento', 'Leitura de instrumentos', 'Procedimentos operacionais']
      },
      {
        name: 'Segurança Operacional',
        topics: ['Gestão de riscos', 'Permissão de trabalho', 'NR-20', 'NR-33', 'NR-35', 'Resposta a emergências']
      }
    ]
  },
  {
    institution: 'TRANSPETRO',
    role: 'Técnico de Manutenção Júnior (Mecânica)',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'medio',
    area: 'administrativo',
    aliases: ['transpetro', 'trans', 'transpetro manutencao mecanica'],
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Concordância', 'Regência', 'Pontuação'] },
      { name: 'Matemática', topics: ['Razão e proporção', 'Porcentagem', 'Equações', 'Geometria', 'Trigonometria básica'] },
      {
        name: 'Mecânica',
        topics: ['Elementos de máquinas', 'Metrologia', 'Desenho técnico', 'Materiais', 'Manutenção de equipamentos rotativos']
      },
      { name: 'Soldagem e Tubulação', topics: ['Processos de soldagem', 'Inspeção visual', 'Leitura de isométricos', 'Tubulações industriais'] },
      { name: 'Segurança do Trabalho', topics: ['NR-12', 'NR-33', 'NR-35', 'Bloqueio e etiquetagem'] }
    ]
  },
  {
    institution: 'TRANSPETRO',
    role: 'Técnico de Manutenção Júnior (Elétrica)',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'medio',
    area: 'administrativo',
    aliases: ['transpetro', 'trans', 'transpetro manutencao eletrica'],
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Concordância', 'Regência', 'Pontuação'] },
      { name: 'Matemática', topics: ['Razão e proporção', 'Porcentagem', 'Equações', 'Funções', 'Trigonometria básica'] },
      {
        name: 'Eletricidade',
        topics: ['Circuitos elétricos', 'Máquinas elétricas', 'Comandos', 'Proteções', 'Instalações industriais']
      },
      { name: 'Instrumentação Básica', topics: ['Sensores', 'Transmissores', 'Malhas de controle', 'Interpretação de P&ID'] },
      { name: 'Segurança do Trabalho', topics: ['NR-10', 'NR-33', 'NR-35'] }
    ]
  },
  {
    institution: 'TRANSPETRO',
    role: 'Engenheiro de Transporte Júnior',
    year: 2025,
    banca: 'Cesgranrio',
    level: 'superior',
    area: 'fiscal',
    aliases: ['transpetro', 'trans', 'engenheiro transporte transpetro'],
    subjects: [
      { name: 'Língua Portuguesa', topics: ['Interpretação', 'Gramática', 'Redação oficial'] },
      { name: 'Inglês Técnico', topics: ['Leitura técnica', 'Vocabulário técnico'] },
      {
        name: 'Logística e Transporte',
        topics: ['Planejamento logístico', 'Gestão de frota', 'Transporte dutoviário', 'Transporte aquaviário', 'Terminais']
      },
      {
        name: 'Gestão de Operações',
        topics: ['Indicadores de desempenho', 'Confiabilidade operacional', 'Gestão de riscos', 'Qualidade']
      },
      { name: 'Segurança de Processos', topics: ['Análise de risco', 'Resposta a emergências', 'Integridade de ativos'] }
    ]
  }
];

// ==================== ÁREAS DISPONÍVEIS ====================

export const AREAS = [
  { id: 'tribunais', name: 'Tribunais (TJ, TRF, TRT, TST)', icon: '⚖️' },
  { id: 'policial', name: 'Policial (PF, PRF, PC, PM)', icon: '🚔' },
  { id: 'fiscal', name: 'Fiscal (Receita, TCU, CGU)', icon: '📊' },
  { id: 'bancario', name: 'Bancário (BB, CEF, BACEN)', icon: '🏦' },
  { id: 'administrativo', name: 'Administrativo (INSS, Prefeituras)', icon: '📋' },
  { id: 'saude', name: 'Saúde (SUS, Hospitais)', icon: '🏥' }
];

// ==================== BANCAS POPULARES ====================

export const BANCAS = [
  'Cebraspe',
  'FGV',
  'FCC',
  'Cesgranrio',
  'VUNESP',
  'IBFC',
  'IADES',
  'Quadrix',
  'Instituto AOCP',
  'FUNDEP',
  'Diversas'
];
